/**
 * Registers the Ethos Costruzioni service worker for PWA installability
 * and offline-fallback support. Only enabled in production builds and
 * over HTTPS (or localhost).
 *
 * Politica di aggiornamento:
 *   - il SW è registrato con updateViaCache: 'none' → il browser controlla
 *     /service-worker.js SEMPRE senza usare la cache HTTP
 *   - ad ogni navigazione facciamo `registration.update()` per rilevare
 *     nuove versioni pubblicate
 *   - appena un nuovo SW è "installed" mandiamo SKIP_WAITING → activate
 *     immediato + reload della pagina, così l'utente prende SEMPRE l'ultimo
 *     bundle JS senza dover svuotare la cache manualmente.
 */

let reloadingForUpdate = false;

export function registerServiceWorker(callbacks = {}) {
  if (typeof window === "undefined") return;
  if (!("serviceWorker" in navigator)) return;
  const isSecure = window.isSecureContext;
  if (!isSecure) return;

  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/service-worker.js", { scope: "/", updateViaCache: "none" })
      .then((registration) => {
        // Controllo aggiornamenti ad ogni caricamento
        registration.update().catch(() => {});

        registration.onupdatefound = () => {
          const installingWorker = registration.installing;
          if (!installingWorker) return;
          installingWorker.onstatechange = () => {
            if (installingWorker.state === "installed") {
              if (navigator.serviceWorker.controller) {
                // C'è un vecchio SW attivo → prendi subito il controllo
                installingWorker.postMessage({ type: "SKIP_WAITING" });
                callbacks.onUpdate?.(registration);
              } else {
                callbacks.onReady?.(registration);
              }
            }
          };
        };
      })
      .catch((err) => {
        console.warn("[SW] registration failed:", err);
      });

    // Quando il nuovo SW ha preso il controllo, ricarico la pagina UNA sola volta
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      if (reloadingForUpdate) return;
      reloadingForUpdate = true;
      window.location.reload();
    });

    // Controlla aggiornamenti anche quando la tab torna in focus
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState !== "visible") return;
      navigator.serviceWorker.getRegistration().then((reg) => {
        if (reg) reg.update().catch(() => {});
      });
    });
  });
}

export async function unregisterServiceWorker() {
  if (!("serviceWorker" in navigator)) return;
  const regs = await navigator.serviceWorker.getRegistrations();
  await Promise.all(regs.map((r) => r.unregister()));
  // Pulisce anche tutte le cache per un reset completo
  if ("caches" in window) {
    const keys = await caches.keys();
    await Promise.all(keys.map((k) => caches.delete(k)));
  }
}
